<script setup lang="ts">
// Vue & Plugins
import { computed, ref } from 'vue'

import { refDebounced } from '@vueuse/core'
import { DateTime } from 'luxon'

import EmployeeAvatar from '../../../employees/components/misc/EmployeeAvatar.vue'

// Composables
import { useEligibleQuery } from '../../../employees/composables/useEmployeeQuery'

import { AreaID } from '../../../areas/models/area'
import { Employee } from '../../../employees/models/employee'
import { ShiftID } from '../../../shifts/models/shift'
// Models
import { SiteID } from '../../../sites/models/site'

const selected = defineModel<Employee | null>('selected', { required: true })

const {
  siteId,
  areaId = undefined,
  startDate,
  endDate,
  shiftId = undefined,
  enabled = true,
} = defineProps<{
  siteId: SiteID
  areaId?: AreaID
  startDate: DateTime
  endDate: DateTime
  shiftId?: ShiftID
  enabled?: boolean
}>()

const isEnabled = computed(() => enabled && !!areaId)

const nameInput = ref('')
const name = refDebounced(nameInput, 500)

const { data, isFetching } = useEligibleQuery(
  () => siteId,
  {
    name,
    shiftId: shiftId,
    areaId: areaId,
    startDate: startDate,
    endDate: endDate,
  },
  {
    enabled: isEnabled,
  },
)

const options = computed<Employee[]>(() => data?.value?.employees ?? [])
</script>

<template>
  <Select
    v-model="selected"
    :options="options"
    :loading="isFetching"
    :disabled="!isEnabled"
    filter
    option-label="displayName"
    placeholder="Select Employee"
    class="h-fit"
    @filter="nameInput = $event.value"
  >
    <template #value="slotProps">
      <template v-if="slotProps.value">
        <div class="flex items-center gap-x-2">
          <EmployeeAvatar
            :avatar="slotProps.value?.avatarUrl"
            :name="slotProps.value?.displayName"
            class="shrink-0"
          />
          <span style="color: var(--p-select-color)">{{
            slotProps.value.displayName
          }}</span>
        </div>
      </template>
      <span v-else>
        {{ slotProps.placeholder }}
      </span>
    </template>
    <template #option="slotProps">
      <div class="flex items-center gap-x-2">
        <EmployeeAvatar
          :avatar="slotProps.option?.avatarUrl"
          :name="slotProps.option?.displayName"
          class="shrink-0"
        />
        <span>{{ slotProps.option.displayName }}</span>
      </div>
    </template>
  </Select>
</template>
